import React from "react";
import { useParams } from "react-router-dom";
import { Anchor, ExternalLink, Fuel, Loader2, MapPin, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { base44 } from "@/api/base44Client";
import NearMeLocationMap from "@/components/public/NearMeLocationMap";

const labels = {
  marinas: "Marinas Near Me",
  anchorages: "Anchorages Near Me",
  fuel: "Boat Fuel Near Me",
  weather: "Marine Weather Near Me",
  services: "Boat Services Near Me",
};

const toRad = (value) => (value * Math.PI) / 180;

const distanceKm = (from, to) => {
  const radius = 6371;
  const dLat = toRad(to.latitude - from.latitude);
  const dLon = toRad(to.longitude - from.longitude);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(from.latitude)) * Math.cos(toRad(to.latitude)) * Math.sin(dLon / 2) ** 2;
  return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const isAnchorageFriendly = (place) => {
  const tags = [...(place.mooring_types || []), ...(place.facilities || []), ...(place.experience_tags || [])].join(" ").toLowerCase();
  return place.protected_anchorage_nearby || tags.includes("anchor") || tags.includes("mooring");
};

const isFuelFriendly = (place) => {
  const tags = [...(place.facilities || []), ...(place.experience_tags || [])].join(" ").toLowerCase();
  return tags.includes("fuel") || tags.includes("diesel") || tags.includes("petrol") || tags.includes("gas") || tags.includes("bunkering");
};

export default function NearMePage({ type = "marinas" }) {
  const { lang = "en" } = useParams();
  const [status, setStatus] = React.useState("");
  const [locating, setLocating] = React.useState(false);
  const [coords, setCoords] = React.useState(null);
  const [nearbyAnchorages, setNearbyAnchorages] = React.useState([]);
  const [nearbyFuelStops, setNearbyFuelStops] = React.useState([]);
  const title = labels[type] || labels.marinas;
  const isAnchoragePage = type === "anchorages";
  const isFuelPage = type === "fuel";

  const askLocation = async () => {
    if (!navigator.geolocation) return setStatus("Location is not available on this device. Try manual search instead.");

    setLocating(true);
    setStatus("Requesting location permission...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const nextCoords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setCoords(nextCoords);
        setStatus(`Location detected: ${nextCoords.latitude.toFixed(5)}, ${nextCoords.longitude.toFixed(5)}`);

        if (isAnchoragePage || isFuelPage) {
          const marinas = await base44.entities.Marina.list("-rating", 500);
          const withCoordinates = marinas.filter((place) => Number.isFinite(Number(place.latitude)) && Number.isFinite(Number(place.longitude)));
          const preferredPlaces = withCoordinates.filter(isFuelPage ? isFuelFriendly : isAnchorageFriendly);
          const sourcePlaces = preferredPlaces.length >= 6 ? preferredPlaces : withCoordinates;
          const closest = sourcePlaces
            .map((place) => {
              const distance = distanceKm(nextCoords, { latitude: Number(place.latitude), longitude: Number(place.longitude) });
              return { ...place, distance_km: distance, distance_nm: distance / 1.852 };
            })
            .sort((a, b) => a.distance_km - b.distance_km)
            .slice(0, 6);
          if (isFuelPage) setNearbyFuelStops(closest);
          else setNearbyAnchorages(closest);
        }

        setLocating(false);
      },
      () => {
        setLocating(false);
        setStatus("No problem — search manually by marina, city, island, or coastline.");
      },
      { enableHighAccuracy: true, timeout: 12000 }
    );
  };

  const mapUrl = coords
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${coords.longitude - 0.08}%2C${coords.latitude - 0.08}%2C${coords.longitude + 0.08}%2C${coords.latitude + 0.08}&layer=mapnik&marker=${coords.latitude}%2C${coords.longitude}`
    : "";
  const openMapUrl = coords ? `https://www.openstreetmap.org/?mlat=${coords.latitude}&mlon=${coords.longitude}#map=13/${coords.latitude}/${coords.longitude}` : "";
  const nearbyResults = isFuelPage ? nearbyFuelStops : nearbyAnchorages;
  const ResultsIcon = isFuelPage ? Fuel : Anchor;
  const resultsTitle = isFuelPage ? "Closest 6 fueling places" : "Closest 6 anchorage places";
  const emptyResults = isFuelPage ? "No fueling places found yet for this area." : "No anchorage places found yet for this area.";

  return (
    <div className="maritime-container py-8 md:py-14">
      <section className="rounded-[2rem] border border-border bg-card p-6 shadow-xl md:p-10">
        <p className="text-sm font-black uppercase tracking-[0.22em] text-primary">Geo-aware sailing search</p>
        <h1 className="mt-4 font-heading text-4xl font-black md:text-6xl">{title}</h1>
        <p className="mt-5 max-w-3xl text-lg leading-8 text-muted-foreground">Find useful marine places near your current position or search manually by destination. Designed for fast mobile use onboard without a heavy live map.</p>
        <div className="mt-8 grid gap-3 md:grid-cols-[auto_1fr_auto]">
          <Button onClick={askLocation} disabled={locating}>{locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />} Use my location</Button>
          <Input placeholder="Search by city, island, marina, or coastline" aria-label="Manual location search" />
          <Button variant="outline">Search</Button>
        </div>
        {status && <p className="mt-4 rounded-2xl bg-muted p-4 text-sm font-semibold text-muted-foreground">{status}</p>}
      </section>

      {(isAnchoragePage || isFuelPage) && coords && (
        <section className="mt-10 grid gap-6 lg:grid-cols-[1fr_24rem]">
          <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-xl">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
              <div>
                <h2 className="text-2xl font-black">Your current location</h2>
                <p className="mt-1 text-sm font-semibold text-muted-foreground">Lat {coords.latitude.toFixed(5)} · Long {coords.longitude.toFixed(5)}</p>
              </div>
              <Button asChild variant="outline" size="sm"><a href={openMapUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /> Open map</a></Button>
            </div>
            <NearMeLocationMap coords={coords} places={nearbyResults} />
          </div>

          <div className="rounded-3xl border border-border bg-card p-5 shadow-xl">
            <h2 className="flex items-center gap-2 text-2xl font-black"><ResultsIcon className="h-5 w-5 text-primary" /> {resultsTitle}</h2>
            <div className="mt-4 space-y-3">
              {nearbyResults.map((place) => (
                <article key={place.id} className="rounded-2xl border border-border bg-muted/30 p-4">
                  <div className="flex items-start gap-3">
                    <MapPin className="mt-1 h-4 w-4 shrink-0 text-primary" />
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-black">{place.name}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">{[place.city, place.region, place.country].filter(Boolean).join(", ")}</p>
                      <p className="mt-2 text-sm font-bold text-primary">{place.distance_nm.toFixed(1)} NM · {place.distance_km.toFixed(1)} km away</p>
                    </div>
                  </div>
                </article>
              ))}
              {!nearbyResults.length && !locating && <p className="rounded-2xl bg-muted p-4 text-sm font-semibold text-muted-foreground">{emptyResults}</p>}
            </div>
          </div>
        </section>
      )}

      <section className="mt-10 grid gap-4 md:grid-cols-3">
        {["Manual search", "Sailor-friendly copy", "Useful next steps"].map((item) => <article key={item} className="rounded-3xl border border-border bg-card p-6"><h2 className="text-xl font-black">{item}</h2><p className="mt-2 text-muted-foreground">Browse related guides, weather pages, marina planning notes, and practical checklists.</p></article>)}
      </section>
      <section className="mt-10 rounded-3xl border border-border bg-muted/35 p-6">
        <h2 className="text-2xl font-black">FAQ</h2>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <article>
            <h3 className="font-black">How do I find marinas near me?</h3>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">Use your current location or search by city, island, marina, or coastline to start planning nearby marina stops.</p>
          </article>
          <article>
            <h3 className="font-black">Can I check fuel and berths?</h3>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">Use marina details as a planning guide, then confirm fuel, visitor berths, VHF channels and arrival rules directly before departure.</p>
          </article>
          <article>
            <h3 className="font-black">Should I verify weather first?</h3>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">Yes. Always compare marina plans with wind, waves, visibility, local warnings and safe daylight before entering or leaving port.</p>
          </article>
        </div>
      </section>
    </div>
  );
}